import { useState } from 'react';
import { useTranslation } from 'react-i18next';
// Restore with the Details buttons below.
// import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { cn } from 'lib/utils';
import { TokenUsageOperationEntry, TokenUsageProviderEntry } from 'features/usage/services/usage-service';
import { UsageDay } from 'features/usage/utils/build-usage-view-model';
import { formatNumber, formatShortDate, getAverage, getRatio } from 'features/usage/utils/usage-format';
import { UsageCardHeader } from './usage-card-header';
import { UsageDailyChart } from './usage-charts';
import { UsageShareList } from './usage-share-list';

const modelPalette = [
  'bg-primary-100 text-true-white border-primary-200',
  'bg-warning-background text-warning-high-emphasis border-warning/20',
  'bg-secondary-100 text-secondary-700 border-secondary-200',
  'bg-pink-100 text-pink-700 border-pink-200',
] as const;

export function UsageDailySection({
  days,
  peakTokens,
  isOwnUsage = false,
}: {
  days: UsageDay[];
  peakTokens: number;
  isOwnUsage?: boolean;
}) {
  const { t } = useTranslation();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  return (
    <div>
      <Card className="rounded-section border-border/70 shadow-none">
        <CardContent className="flex flex-col gap-5 p-5 md:p-6">
          <div className="flex items-start justify-between gap-3">
            <UsageCardHeader
              title={t('DAILY_ACTIVITY')}
              subtitle={
                isOwnUsage
                  ? t('TOKENS_YOU_USED_EACH_DAY', { defaultValue: 'Tokens you used each day' })
                  : t('TOKENS_USED_EACH_DAY', { defaultValue: 'Tokens used each day' })
              }
            />
            {/* Details hidden for now; may be needed later.
            <Button variant="outline" size="sm" className="shrink-0" onClick={() => setIsDetailsOpen(true)}>
              {t('DETAILS')}
            </Button>
            */}
          </div>
          <UsageDailyChart days={days} />
        </CardContent>
      </Card>

      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="!flex max-h-[85vh] max-w-5xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b border-border px-6 py-5">
            <DialogTitle>{t('DAILY_ACTIVITY')}</DialogTitle>
            <DialogDescription className="sr-only">{t('DAILY_USAGE_SUBTITLE')}</DialogDescription>
          </DialogHeader>
          <div className="shrink-0 border-b border-border px-6">
            <table className="w-full table-fixed">
              <thead>
                <tr className="h-12">
                  <th className="align-middle pr-4 text-left text-xs font-medium text-muted-foreground">
                    {t('DATE')}
                  </th>
                  <th className="align-middle px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('INPUT')}
                  </th>
                  <th className="align-middle px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('OUTPUT')}
                  </th>
                  <th className="align-middle px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('TOTAL')}
                  </th>
                  <th className="align-middle px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('TURNS')}
                  </th>
                  <th className="align-middle pl-4 text-left text-xs font-medium text-muted-foreground">
                    {t('RATIO')}
                  </th>
                </tr>
              </thead>
            </table>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-5">
            <table className="w-full table-fixed">
              <tbody>
                {days.map((day) => (
                  <tr
                    key={day.date}
                    className={cn('h-14 border-t border-border', day.inactive && 'opacity-35')}
                  >
                    <td className="align-middle py-3 pr-4 font-mono text-xs text-muted-foreground">
                      {formatShortDate(day.date)}
                      {!day.inactive && day.total_tokens === peakTokens && (
                        <div className="mt-1 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                          {t('PEAK')}
                        </div>
                      )}
                    </td>
                    {day.inactive ? (
                      <td colSpan={5} className="align-middle px-4 py-3 text-sm text-muted-foreground">
                        {t('NO_ACTIVITY')}
                      </td>
                    ) : (
                      <>
                        <td className="align-middle px-4 py-3 font-mono text-xs text-primary dark:text-true-white">
                          {formatNumber(day.input_tokens)}
                          <div className="mt-1 text-[10px] text-muted-foreground">
                            {t('AVG_SHORT', { avg: getAverage(day.input_tokens, day.turn_count) })}
                          </div>
                        </td>
                        <td className="align-middle px-4 py-3 font-mono text-xs text-success-green">
                          {formatNumber(day.output_tokens)}
                          <div className="mt-1 text-[10px] text-muted-foreground">
                            {t('AVG_SHORT', { avg: getAverage(day.output_tokens, day.turn_count) })}
                          </div>
                        </td>
                        <td className="align-middle px-4 py-3 font-mono text-xs text-foreground">
                          {formatNumber(day.total_tokens)}
                        </td>
                        <td className="align-middle px-4 py-3 font-mono text-xs text-foreground">
                          {formatNumber(day.turn_count)}
                        </td>
                        <td className="align-middle py-3 pl-4 font-mono text-xs text-muted-foreground">
                          {getRatio(day.input_tokens, day.output_tokens)}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const operationLabelKeys: Record<string, string> = {
  conversation: 'OPERATION_CONVERSATION',
  title_generation: 'OPERATION_TITLE_GENERATION',
  self_improvement_review: 'OPERATION_SELF_IMPROVEMENT',
  image_generation: 'OPERATION_IMAGE_GENERATION',
  model_routing: 'OPERATION_MODEL_ROUTING',
};

export function UsageOperationSection({
  operations,
  totalTokens,
  isOwnUsage = false,
}: {
  operations: TokenUsageOperationEntry[];
  totalTokens: number;
  isOwnUsage?: boolean;
}) {
  const { t } = useTranslation();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  if (!operations.length) return null;

  return (
    <div className="flex h-full flex-col">
      <Card className="flex-1 rounded-section border-border/70 shadow-none">
        <CardContent className="flex h-full flex-col gap-5 p-5 md:p-6">
          <div className="flex items-start justify-between gap-3">
            <UsageCardHeader
              title={t('BY_OPERATION')}
              subtitle={
                isOwnUsage
                  ? t('OPERATION_SUBTITLE_PERSONAL', { defaultValue: 'What your tokens were spent on' })
                  : t('TOKEN_SHARE')
              }
            />
            {/* Details hidden for now; may be needed later.
            <Button variant="outline" size="sm" className="shrink-0" onClick={() => setIsDetailsOpen(true)}>
              {t('DETAILS')}
            </Button>
            */}
          </div>
          <UsageShareList
            items={operations.map((operation) => ({
              key: operation.operation,
              label: t(operationLabelKeys[operation.operation] ?? operation.operation),
              value: formatNumber(operation.total_tokens),
              share: totalTokens ? (operation.total_tokens / totalTokens) * 100 : 0,
            }))}
          />
        </CardContent>
      </Card>

      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="!flex max-h-[85vh] w-[min(96vw,80rem)] !max-w-7xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b border-border px-6 py-5">
            <DialogTitle>{t('BY_OPERATION')}</DialogTitle>
            <DialogDescription className="sr-only">{t('TOKEN_SHARE')}</DialogDescription>
          </DialogHeader>
          <div className="shrink-0 border-b border-border px-6">
            <table className="w-full table-fixed">
              <colgroup>
                <col className="w-[32%]" />
                <col className="w-[13%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[10%]" />
              </colgroup>
              <thead>
                <tr className="h-12">
                  <th className="align-middle pr-4 text-left text-xs font-medium text-muted-foreground">
                    {t('OPERATION')}
                  </th>
                  <th className="align-middle px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('MODEL_CALLS')}
                  </th>
                  <th className="align-middle px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('INPUT_TOKENS')}
                  </th>
                  <th className="align-middle px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('OUTPUT_TOKENS')}
                  </th>
                  <th className="align-middle px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('TOTAL_TOKENS')}
                  </th>
                  <th className="align-middle pl-4 text-left text-xs font-medium text-muted-foreground">
                    {t('SHARE')}
                  </th>
                </tr>
              </thead>
            </table>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-5">
            <table className="w-full table-fixed">
              <colgroup>
                <col className="w-[32%]" />
                <col className="w-[13%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[10%]" />
              </colgroup>
              <tbody>
                {operations.map((operation, index) => {
                  const share = totalTokens ? (operation.total_tokens / totalTokens) * 100 : 0;
                  return (
                    <tr key={operation.operation} className="border-t border-border">
                      <td className="py-3 pr-4">
                        <span
                          className={cn(
                            'inline-flex rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] dark:text-true-white',
                            modelPalette[index % modelPalette.length]
                          )}
                        >
                          {t(operationLabelKeys[operation.operation] ?? operation.operation)}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground">
                        {formatNumber(operation.model_call_count)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-primary dark:text-true-white">
                        {formatNumber(operation.input_tokens)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-success-green">
                        {formatNumber(operation.output_tokens)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground">
                        {formatNumber(operation.total_tokens)}
                      </td>
                      <td className="py-3 pl-4 font-mono text-lg text-warning-high-emphasis">
                        {share.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function UsageProviderSection({
  providers,
  isOwnUsage = false,
}: {
  providers: TokenUsageProviderEntry[];
  isOwnUsage?: boolean;
}) {
  const { t } = useTranslation();
  if (!providers.length) return null;

  const totalCalls = providers.reduce((sum, provider) => sum + provider.model_call_count, 0);

  return (
    <div className="flex h-full flex-col">
      <Card className="flex-1 rounded-section border-border/70 shadow-none">
        <CardContent className="flex h-full flex-col gap-5 p-5 md:p-6">
          <UsageCardHeader
            title={t('BY_PROVIDER')}
            subtitle={
              isOwnUsage
                ? t('PROVIDER_SUBTITLE_PERSONAL', { defaultValue: 'Share of your model calls' })
                : t('MODEL_CALL_SHARE')
            }
          />
          <UsageShareList
            items={providers.map((provider) => ({
              key: provider.provider,
              label: provider.provider,
              value: `${formatNumber(provider.model_call_count)} ${t('CALLS')}`,
              share: totalCalls ? (provider.model_call_count / totalCalls) * 100 : 0,
            }))}
          />
        </CardContent>
      </Card>
    </div>
  );
}
